import type { AudioWithChapters } from "@brookbush/types";

import * as Slider from "@radix-ui/react-slider";
import clsx from "clsx";
import { ListMusic } from "lucide-react";
import { useState } from "react";
import {
	FaBackward,
	FaForward,
	FaPauseCircle,
	FaPlayCircle,
} from "react-icons/fa";
import { RiForward10Fill, RiReplay10Fill } from "react-icons/ri";
import type { useAudio } from "react-use";

import AudioPlayerRate from "./AudioPlayerRate";

function formatTime(seconds: number) {
	const h = Math.floor(seconds / 3600);
	const m = Math.floor((seconds % 3600) / 60);
	const s = Math.floor(seconds % 60);

	const pad = (n: any) => n.toString().padStart(2, "0");

	return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}
type Props = AudioWithChapters & {
	isModal?: boolean;
	duration?: number;
	currentChapterIndex: number;
	audioPlayer: HTMLAudioElement;
	controls: ReturnType<typeof useAudio>[2];

	state: ReturnType<typeof useAudio>[1];
};

const AudioPlayerCard = ({
	audio,
	chapters,
	currentChapterIndex,
	audioPlayer,
	state,
	duration,
	controls,
	isModal,
}: Props) => {
	const [isExpanded, setIsExpanded] = useState(false);
	return (
		<div
			key={audio._id}
			id="audio-player"
			className={clsx({
				"relative mx-auto w-full scroll-m-32 rounded-xl border phablet:bg-white/60 px-3.5 pt-3.5 pb-4 shadow shadow-black/5 dark:bg-slate-700/50":
					!isModal,
			})}
		>
			<div
				className={clsx(
					"no-scrollbar mx-2 overflow-x-scroll whitespace-nowrap text-center font-medium",
					isModal ? "hidden" : "block",
				)}
			>
				{chapters[currentChapterIndex]?.title}
			</div>

			<div className="my-3 flex w-full items-center gap-2 font-medium text-sm">
				<span className="flex w-12 shrink-0 items-center justify-center">
					{formatTime(state.time || 0)}
				</span>

				<Slider.Root
					className="relative flex h-5 w-full cursor-pointer touch-none select-none items-center"
					value={[audioPlayer?.currentTime]}
					max={duration}
					step={1}
					onValueChange={(val) => {
						audioPlayer.currentTime = val[0];
					}}
				>
					<Slider.Track className="relative h-1.5 grow rounded-full bg-slate-200/70 dark:bg-slate-600">
						<Slider.Range className="absolute h-full rounded-l-full bg-sky-500" />
					</Slider.Track>

					<Slider.Thumb
						className="relative block h-3.5 w-3.5 cursor-pointer rounded-full bg-sky-400 shadow-black/20 shadow-md hover:scale-110 dark:bg-sky-500"
						aria-label="Player"
					/>
				</Slider.Root>

				{duration && !Number.isNaN(duration) && (
					<span className="flex w-12 shrink-0 items-center justify-center">
						{formatTime(duration)}
					</span>
				)}
			</div>

			<div className="flex w-full items-center gap-1">
				<div className="flex flex-1 justify-center">
					<AudioPlayerRate
						playbackRate={audioPlayer?.playbackRate ?? 1}
						onPlaybackRateChange={(playbackRate) => {
							audioPlayer.playbackRate = playbackRate;
						}}
					/>
				</div>

				<div className="flex w-4/7 items-center justify-center">
					<button
						disabled={!state.time}
						onClick={() => {
							audioPlayer.currentTime -= 10;
						}}
						className="cursor-pointer rounded-full p-2 hover:bg-slate-100 disabled:pointer-events-none disabled:opacity-75 disabled:hover:bg-transparent dark:hover:bg-slate-600"
					>
						<RiReplay10Fill size={24} />
					</button>

					<button
						disabled={!currentChapterIndex}
						className="cursor-pointer rounded-full p-2 hover:bg-slate-100 disabled:pointer-events-none disabled:opacity-75 disabled:hover:bg-transparent dark:hover:bg-slate-600"
						onClick={() => {
							audioPlayer!.currentTime =
								chapters[currentChapterIndex - 1].audioTimestamp;
						}}
					>
						<FaBackward size={22} className="mr-0.5" />
					</button>

					<button
						className="mx-1 cursor-pointer rounded-full hover:text-slate-600 disabled:opacity-75 disabled:hover:bg-transparent dark:hover:text-slate-300"
						onClick={async () =>
							state.playing ? await controls.pause() : await controls.play()
						}
					>
						{state.playing ? (
							<FaPauseCircle size={44} />
						) : (
							<FaPlayCircle size={44} />
						)}
					</button>

					<button
						disabled={currentChapterIndex === chapters.length - 1}
						onClick={() => {
							audioPlayer.currentTime =
								chapters[currentChapterIndex + 1].audioTimestamp;
						}}
						className="cursor-pointer rounded-full p-2 hover:bg-slate-100 disabled:pointer-events-none disabled:opacity-75 disabled:hover:bg-transparent dark:hover:bg-slate-600"
					>
						<FaForward size={22} />
					</button>

					<button
						onClick={() => {
							audioPlayer.currentTime += 10;
						}}
						className="cursor-pointer rounded-full p-2 hover:bg-slate-100 disabled:pointer-events-none disabled:opacity-75 disabled:hover:bg-transparent dark:hover:bg-slate-600"
					>
						<RiForward10Fill size={24} />
					</button>
				</div>

				<div className="flex flex-1 justify-center">
					<button
						onClick={() => setIsExpanded(!isExpanded)}
						className={clsx(
							"mx-auto ml-auto flex cursor-pointer items-center rounded-full p-2 text-sm hover:bg-slate-50 dark:text-white dark:hover:bg-slate-700",
						)}
					>
						<ListMusic size={22} />
					</button>
				</div>
			</div>

			{isExpanded && (
				<div
					className={clsx(
						"mt-4 flex flex-col gap-2 rounded-xl border bg-slate-100 px-4 py-2.5",
						isModal ? "dark:bg-slate-700/50" : "dark:bg-slate-800",
					)}
				>
					{chapters.map((chapter, i) => (
						<button
							onClick={() => {
								audioPlayer.currentTime = chapters[i].audioTimestamp;
							}}
							className={clsx("flex text-left text-base hover:underline", {
								"font-semibold": i === currentChapterIndex,
							})}
							key={chapter._key}
						>
							<span>
								{i + 1}. {chapter.title}{" "}
							</span>
						</button>
					))}
				</div>
			)}
		</div>
	);
};

export default AudioPlayerCard;
